import { useEffect, useRef, useState } from 'preact/hooks';
import type { MutableRef } from 'preact/hooks';
import type { Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { ApiError, createUpgradePaymentSetup } from '@/api/client';
import { getStripe, buildStripeAppearance, paymentElementWalletsSuppressed, paymentElementMethodOrder } from '@/api/stripe-loader';
import { getStripeLocale } from '@/i18n/loader';
import { hasKey, t, useT } from '@/i18n/t';
import { logError } from '@/sdkConsole';
import { TestCardHint } from '@/components/TestCardHint';
import { useStripeElementSlot } from '@/components/useStripeElementSlot';

// The step's single Confirm button drives verification through this handle.
export interface UpgradeCardHandle {
  // Resolves the succeeded SetupIntent id, or null after showing why the card
  // was not verified. Never charges.
  verify: () => Promise<string | null>;
}

interface Props {
  // Read once per load: the SetupIntent is bound to the subscription, so a
  // re-quoted attempt keeps using it.
  upgradeId: string;
  disabled: boolean;
  autoFocus?: boolean;
  handleRef: MutableRef<UpgradeCardHandle | null>;
  onCompleteChange: (complete: boolean) => void;
}

function setupErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return t('upgrade.payment.loadFailed');
  }
  if (err.status === 401) {
    return t('upgrade.reason.session_expired');
  }
  if (err.status === 403) {
    return t('upgrade.reason.forbidden');
  }
  const key = `upgrade.reason.${err.code}`;
  return err.code && hasKey(key) ? t(key) : t('upgrade.payment.loadFailed');
}

// UpgradePaymentForm collects a replacement card for an upgrade on a
// card-only SetupIntent. Opening or abandoning it never charges or changes the
// subscription's default; the server adopts the card only at Confirm.
export function UpgradePaymentForm({ upgradeId, disabled, autoFocus = true, handleRef, onCompleteChange }: Props) {
  useT();
  const slotRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const paymentElRef = useRef<StripePaymentElement | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const mounted = useRef(true);
  const completeRef = useRef(onCompleteChange);
  completeRef.current = onCompleteChange;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      handleRef.current = null;
      completeRef.current(false);
    };
  }, []);

  const slotName = useStripeElementSlot(slotRef, async (mountDiv, cancelled) => {
    let stripe: Stripe;
    let clientSecret: string;
    try {
      const setup = await createUpgradePaymentSetup(upgradeId);
      if (cancelled()) {
        return;
      }
      clientSecret = setup.clientSecret;
      stripe = await getStripe(setup.publishableKey, setup.providerAccount);
    } catch (err) {
      logError('upgrade card form failed to load', err);
      if (!cancelled()) {
        setLoadError(setupErrorMessage(err));
      }
      return;
    }
    if (cancelled()) {
      return;
    }
    let elements: StripeElements;
    let paymentEl: StripePaymentElement;
    try {
      elements = stripe.elements({ clientSecret, appearance: buildStripeAppearance(slotRef.current), locale: getStripeLocale() });
      paymentEl = elements.create('payment', { layout: 'tabs', paymentMethodOrder: paymentElementMethodOrder, wallets: paymentElementWalletsSuppressed });
    } catch (err) {
      logError('upgrade card element could not be created', err);
      setLoadError(t('upgrade.payment.loadFailed'));
      return;
    }
    paymentEl.on('change', (event) => {
      if (cancelled()) {
        return;
      }
      setCardError(null);
      completeRef.current(event.complete);
    });
    paymentEl.on('ready', () => {
      if (cancelled()) {
        return;
      }
      setReady(true);
      if (autoFocus) {
        paymentEl.focus();
      }
    });
    paymentEl.on('loaderror', (event) => {
      if (cancelled()) {
        return;
      }
      logError('upgrade card element failed to load', event.error);
      setLoadError(t('upgrade.payment.loadFailed'));
    });
    try {
      paymentEl.mount(mountDiv);
    } catch (err) {
      logError('upgrade card element mount failed', err);
      paymentEl.destroy();
      setLoadError(t('upgrade.payment.loadFailed'));
      return;
    }
    stripeRef.current = stripe;
    elementsRef.current = elements;
    paymentElRef.current = paymentEl;
    return () => {
      completeRef.current(false);
      paymentEl.destroy();
      stripeRef.current = null;
      elementsRef.current = null;
      paymentElRef.current = null;
    };
  }, [attempt]);

  useEffect(() => {
    paymentElRef.current?.update({ readOnly: disabled });
  }, [disabled, ready]);

  handleRef.current = {
    verify: async () => {
      const stripe = stripeRef.current;
      const elements = elementsRef.current;
      if (!stripe || !elements) {
        setCardError(t('upgrade.payment.loadFailed'));
        return null;
      }
      setCardError(null);
      try {
        // Card-only SetupIntents never need a redirect; 3DS runs in Stripe's
        // in-page frame. return_url is still required by Stripe.js.
        const result = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
          confirmParams: { return_url: window.location.href, payment_method_data: { allow_redisplay: 'always' } },
        });
        if (!mounted.current) {
          return null;
        }
        if (result.error) {
          setCardError(result.error.message ?? t('upgrade.payment.setupFailed'));
          return null;
        }
        if (result.setupIntent?.status !== 'succeeded' || !result.setupIntent.id) {
          setCardError(t('upgrade.payment.setupFailed'));
          return null;
        }
        return result.setupIntent.id;
      } catch (err) {
        logError('upgrade card setup failed', err);
        if (mounted.current) {
          setCardError(t('upgrade.payment.setupFailed'));
        }
        return null;
      }
    },
  };

  const retry = () => {
    setLoadError(null);
    setReady(false);
    setAttempt((value) => value + 1);
  };

  return (
    <div class="sm-upgrade-card-form">
      <TestCardHint />
      {!loadError && <div ref={slotRef} class="sm-payment-slot"><slot name={slotName} /></div>}
      {loadError && <p role="alert" class="sm-error">{loadError}</p>}
      {loadError && <button type="button" class="sm-btn sm-btn-secondary" disabled={disabled} onClick={retry}>{t('checkout.tryAgain')}</button>}
      {cardError && <p role="alert" class="sm-error">{cardError}</p>}
    </div>
  );
}
